/**
 * Review taxonomy — the severity / category vocabularies the overlay offers.
 *
 * These MUST stay in lockstep with the backend's `ReviewSeverity` /
 * `ReviewCategory` Pydantic enums (mirrored in api/client.ts). They match the
 * source review tool's taxonomy: severity {blocker, change-needed, suggestion,
 * question}; category {copy, ux, flow, data, role-model, scope}.
 */
import type { ReviewCategory, ReviewSeverity } from "@/api/client";

export const SEVERITIES: readonly { value: ReviewSeverity; label: string }[] = [
  { value: "blocker", label: "Blocker" },
  { value: "change-needed", label: "Change needed" },
  { value: "suggestion", label: "Suggestion" },
  { value: "question", label: "Question" },
];

export const CATEGORIES: readonly { value: ReviewCategory; label: string }[] = [
  { value: "copy", label: "Copy" },
  { value: "ux", label: "UX" },
  { value: "flow", label: "Flow" },
  { value: "data", label: "Data" },
  { value: "role-model", label: "Role model" },
  { value: "scope", label: "Scope" },
];

/** Common quick-pick tags. Free choice on the wire (string[]); these are just
 * a curated starter set surfaced as toggles in the form. */
export const TAG_SUGGESTIONS: readonly string[] = [
  "needs-design",
  "needs-spec",
  "regression",
  "accessibility",
  "mobile",
  "edge-case",
];

export const SEVERITY_LABEL: Record<ReviewSeverity, string> = Object.fromEntries(
  SEVERITIES.map((s) => [s.value, s.label]),
) as Record<ReviewSeverity, string>;

export const CATEGORY_LABEL: Record<ReviewCategory, string> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.label]),
) as Record<ReviewCategory, string>;

/** Relative-time formatter for comment timestamps (e.g. "3m ago"). */
export function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-AU");
}

/**
 * Tiny markdown-ish renderer for comment bodies. Escapes HTML first, then
 * applies a deliberately small set of inline rules: **bold**, *italic*,
 * `code`. Newlines are preserved via CSS (white-space: pre-wrap), so we don't
 * need block handling. Returns an HTML string for dangerouslySetInnerHTML —
 * safe because every `<` `>` `&` is escaped before any tag is introduced.
 */
export function renderBody(raw: string): string {
  const escaped = raw.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}
